import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Target, ChevronDown, ChevronRight, Search, Loader2, AlertTriangle, Wallet, Layers } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { clsx } from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { BudgetWithProgress, Category, CreateBudgetDto } from '../../types';
import api from '../../services/api';
import OptionToggle from '../common/OptionToggle';
import SegmentedControl from '../common/SegmentedControl';
import SwitchToggle from '../common/SwitchToggle';
import FloatingField from '../common/FloatingField';

// Helper pour convertir kebab-case en PascalCase
function toPascalCase(str: string): string {
  return str
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

// Composant pour rendre une icône Lucide dynamiquement
function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const pascalName = toPascalCase(name);
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[pascalName];

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} />;
}

type BankAccountOption = { id: string; name: string; color: string };

interface BudgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  type: 'shared' | 'personal';
  editingBudget: BudgetWithProgress | null;
  expenseCategories?: Category[];
  bankAccounts?: { id: string; name: string; color: string }[];
  month?: number; // Mois affiché (1-12)
  year?: number;  // Année affichée
  /** Bascule vers l'onglet Enveloppe (création). Absent = pas d'onglets. */
  onSwitchType?: () => void;
}

// Helper pour obtenir les semaines d'un mois avec leurs dates de début/fin
// Règle : une semaine appartient au mois de son lundi
function getWeeksOfMonth(month: number, year: number): { week: number; start: string; end: string }[] {
  const weeks: { week: number; start: string; end: string }[] = [];
  const firstDay = new Date(year, month - 1, 1);

  // Trouver le premier lundi du mois
  let firstMonday = new Date(firstDay);
  const dayOfWeek = firstMonday.getDay(); // 0 = dimanche, 1 = lundi, ...
  if (dayOfWeek !== 1) {
    // Si le 1er n'est pas un lundi, avancer jusqu'au premier lundi
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    firstMonday.setDate(firstMonday.getDate() + daysUntilMonday);
  }

  const formatDate = (d: Date) => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;

  let weekNumber = 1;
  let currentMonday = new Date(firstMonday);

  // Parcourir tous les lundis du mois
  while (currentMonday.getMonth() === month - 1) {
    // La semaine va du lundi au dimanche (peut déborder sur le mois suivant)
    const weekStart = new Date(currentMonday);
    const weekEnd = new Date(currentMonday);
    weekEnd.setDate(weekEnd.getDate() + 6); // Dimanche

    weeks.push({
      week: weekNumber,
      start: formatDate(weekStart),
      end: formatDate(weekEnd),
    });

    weekNumber++;
    // Passer au lundi suivant
    currentMonday.setDate(currentMonday.getDate() + 7);
  }

  return weeks;
}

const defaultFormData: CreateBudgetDto = {
  expenseCategoryId: '',
  expenseSubcategoryId: '',
  expenseCategoryIds: [],
  expenseSubcategoryIds: [],
  name: '',
  amount: 0,
  period: 'monthly',
  weeklyAmounts: null,
  bankAccountIds: null,
  startDate: new Date().toISOString().split('T')[0],
  endDate: null,
};

export default function BudgetConfigModal({
  isOpen,
  onClose,
  onSaveSuccess,
  type,
  editingBudget,
  expenseCategories: propCategories,
  bankAccounts: propBankAccounts,
  month: propMonth,
  year: propYear,
  onSwitchType,
}: BudgetConfigModalProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const { translateCategoryName } = useCategoryTranslation();
  const fmt = (n: number) =>
    new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 0 }).format(Math.round(n || 0));
  // Utiliser le mois/année fourni ou le mois en cours par défaut
  const now = new Date();
  const displayMonth = propMonth || (now.getMonth() + 1);
  const displayYear = propYear || now.getFullYear();
  const weeksOfMonth = getWeeksOfMonth(displayMonth, displayYear);
  const [formData, setFormData] = useState<CreateBudgetDto>(defaultFormData);
  const [useWeeklyAmounts, setUseWeeklyAmounts] = useState(false);
  const [useEndDate, setUseEndDate] = useState(false);
  // Un budget est imputé au solde d'un compte précis : un seul compte par budget.
  // `legacyMultiAccount` signale un budget créé avant cette règle (tous les comptes,
  // ou plusieurs comptes) : il faut lui en choisir un pour l'enregistrer.
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [legacyMultiAccount, setLegacyMultiAccount] = useState(false);
  const [weeklyAmounts, setWeeklyAmounts] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [categories, setCategories] = useState<Category[]>(propCategories || []);
  // Les deux natures de comptes sont proposées : c'est le compte choisi qui décide si le
  // budget est personnel (opérations personnelles) ou commun (opérations du compte commun).
  const [personalAccounts, setPersonalAccounts] = useState<BankAccountOption[]>(
    type === 'personal' ? (propBankAccounts || []) : []
  );
  const [sharedAccounts, setSharedAccounts] = useState<BankAccountOption[]>(
    type === 'shared' ? (propBankAccounts || []) : []
  );
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownOpen(false);
        setCategorySearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Charger les catégories et comptes si non fournis
  useEffect(() => {
    if (!isOpen) return;

    // Un compte commun est inaccessible hors couple : on retombe alors sur une liste vide
    const fetchAccounts = async (endpoint: string): Promise<BankAccountOption[]> => {
      try {
        const response = await api.get(endpoint);
        if (!response.data.success) return [];
        return response.data.data.map((a: BankAccountOption) => ({
          id: a.id,
          name: a.name,
          color: a.color,
        }));
      } catch {
        return [];
      }
    };

    const loadData = async () => {
      setLoadingData(!propCategories?.length);
      try {
        // Charger les catégories si non fournies
        if (!propCategories?.length) {
          const catResponse = await api.get('/categories/expense');
          if (catResponse.data.success) {
            setCategories(catResponse.data.data);
          }
        }

        // Les comptes de la nature courante peuvent venir du parent, l'autre liste est chargée
        const [personal, shared] = await Promise.all([
          type === 'personal' && propBankAccounts?.length
            ? Promise.resolve(propBankAccounts)
            : fetchAccounts('/bank-accounts'),
          type === 'shared' && propBankAccounts?.length
            ? Promise.resolve(propBankAccounts)
            : fetchAccounts('/shared-bank-accounts'),
        ]);
        setPersonalAccounts(personal);
        setSharedAccounts(shared);
      } catch (err) {
        console.error('Erreur chargement données:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [isOpen, type, propCategories, propBankAccounts]);

  // Synchroniser avec les props
  useEffect(() => {
    if (propCategories?.length) setCategories(propCategories);
  }, [propCategories]);

  useEffect(() => {
    if (!propBankAccounts?.length) return;
    if (type === 'personal') setPersonalAccounts(propBankAccounts);
    else setSharedAccounts(propBankAccounts);
  }, [propBankAccounts, type]);

  const expenseCategories = categories;

  // Détecter si la modification va créer un versionnement
  const versioningInfo = useMemo(() => {
    if (!editingBudget || !editingBudget.startDate) {
      return null;
    }

    // Extraire le mois/année de la date de début du budget
    const startDate = new Date(editingBudget.startDate);
    const budgetStartMonth = startDate.getMonth() + 1;
    const budgetStartYear = startDate.getFullYear();

    // Comparer avec le mois affiché
    const displayDateNum = displayYear * 100 + displayMonth;
    const budgetStartDateNum = budgetStartYear * 100 + budgetStartMonth;

    if (displayDateNum > budgetStartDateNum) {
      // Calculer le dernier jour du mois précédent
      const prevMonth = displayMonth === 1 ? 12 : displayMonth - 1;
      const prevYear = displayMonth === 1 ? displayYear - 1 : displayYear;
      const lastDayPrevMonth = new Date(prevYear, prevMonth, 0).getDate();

      const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

      return {
        willVersion: true,
        oldEndDate: `${lastDayPrevMonth} ${tc(`months.${MONTH_KEYS[prevMonth - 1]}`)} ${prevYear}`,
        newStartDate: `1 ${tc(`months.${MONTH_KEYS[displayMonth - 1]}`)} ${displayYear}`,
      };
    }

    return null;
  }, [editingBudget, displayMonth, displayYear]);

  // Charger les données du budget en édition
  useEffect(() => {
    if (editingBudget) {
      // Utiliser les tableaux de multi-sélection s'ils existent, sinon les valeurs simples
      const categoryIds = editingBudget.expenseCategoryIds?.length
        ? editingBudget.expenseCategoryIds
        : (editingBudget.expenseCategoryId ? [editingBudget.expenseCategoryId] : []);
      const subcategoryIds = editingBudget.expenseSubcategoryIds?.length
        ? editingBudget.expenseSubcategoryIds
        : (editingBudget.expenseSubcategoryId ? [editingBudget.expenseSubcategoryId] : []);

      setFormData({
        expenseCategoryId: editingBudget.expenseCategoryId || '',
        expenseSubcategoryId: editingBudget.expenseSubcategoryId || '',
        expenseCategoryIds: categoryIds,
        expenseSubcategoryIds: subcategoryIds,
        name: editingBudget.name !== editingBudget.categoryName ? editingBudget.name : '',
        amount: editingBudget.amount,
        period: editingBudget.period,
        weeklyAmounts: editingBudget.weeklyAmounts,
        bankAccountIds: editingBudget.bankAccountIds,
        startDate: editingBudget.startDate,
        endDate: editingBudget.endDate,
      });
      setUseWeeklyAmounts(!!editingBudget.weeklyAmounts);
      setUseEndDate(!!editingBudget.endDate);
      // Un seul compte ciblé → sélection reprise telle quelle. Tous les comptes ou
      // plusieurs comptes → périmètre hérité, l'utilisateur doit trancher.
      const budgetAccountIds = editingBudget.bankAccountIds || [];
      if (budgetAccountIds.length === 1) {
        setSelectedAccountId(budgetAccountIds[0]);
        setLegacyMultiAccount(false);
      } else {
        setSelectedAccountId('');
        setLegacyMultiAccount(true);
      }
      if (editingBudget.weeklyAmounts) {
        setWeeklyAmounts(editingBudget.weeklyAmounts);
      }
    } else {
      // Pour un nouveau budget, la date de début est le 1er du mois affiché
      const firstDayOfDisplayMonth = `${displayYear}-${displayMonth.toString().padStart(2, '0')}-01`;
      setFormData({
        ...defaultFormData,
        startDate: firstDayOfDisplayMonth,
      });
      setUseWeeklyAmounts(false);
      setUseEndDate(false);
      setSelectedAccountId('');
      setLegacyMultiAccount(false);
      // Initialiser les montants pour chaque semaine du mois
      const initialWeeklyAmounts: Record<number, number> = {};
      weeksOfMonth.forEach(w => { initialWeeklyAmounts[w.week] = 0; });
      setWeeklyAmounts(initialWeeklyAmounts);
    }
    setError(null);
    setCategorySearch('');
    setExpandedCategories([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBudget, isOpen, displayMonth, displayYear]);

  if (!isOpen) return null;

  // Récupérer les catégories sélectionnées
  const selectedCategoryIds = formData.expenseCategoryIds || [];
  const selectedSubcategoryIds = formData.expenseSubcategoryIds || [];

  // Construire la liste des sous-catégories disponibles (groupées par catégorie sélectionnée)
  const availableSubcategories = expenseCategories
    .filter(c => selectedCategoryIds.includes(c.id))
    .flatMap(c => c.subcategories.map(sub => ({ ...sub, categoryId: c.id, categoryName: c.name })));

  // Calculer la somme des montants hebdomadaires
  const weeklyTotal = Object.values(weeklyAmounts).reduce((sum, val) => sum + (val || 0), 0);

  // Périmètre d'une catégorie dans l'arbre :
  // - 'all'     : catégorie prise en entier (aucune sous-catégorie listée)
  // - 'partial' : catégorie restreinte à certaines de ses sous-catégories
  // - 'none'    : hors budget
  const getCategoryState = (cat: Category): 'all' | 'partial' | 'none' => {
    if (!selectedCategoryIds.includes(cat.id)) return 'none';
    const picked = cat.subcategories.filter(s => selectedSubcategoryIds.includes(s.id));
    return picked.length === 0 ? 'all' : 'partial';
  };

  // Une catégorie prise en entier couvre toutes ses sous-catégories, sans les lister
  const isSubcategorySelected = (cat: Category, subcategoryId: string) =>
    getCategoryState(cat) === 'all' || selectedSubcategoryIds.includes(subcategoryId);

  const applySelection = (categoryIds: string[], subcategoryIds: string[]) => {
    setFormData({
      ...formData,
      expenseCategoryIds: categoryIds,
      expenseSubcategoryIds: subcategoryIds,
      // Mettre à jour les champs simples pour rétro-compatibilité
      expenseCategoryId: categoryIds[0] || '',
      expenseSubcategoryId: subcategoryIds[0] || '',
    });
  };

  // Clic sur une catégorie : la prendre en entier, ou la retirer avec ses sous-catégories
  const handleCategoryToggle = (cat: Category) => {
    const subIds = cat.subcategories.map(s => s.id);
    const otherSubIds = selectedSubcategoryIds.filter(id => !subIds.includes(id));

    if (getCategoryState(cat) === 'none') {
      applySelection([...selectedCategoryIds, cat.id], otherSubIds);
    } else {
      applySelection(selectedCategoryIds.filter(id => id !== cat.id), otherSubIds);
    }
  };

  // Clic sur une sous-catégorie : la catégorie bascule sur un périmètre partiel
  const handleSubcategoryToggle = (cat: Category, subcategoryId: string) => {
    const subIds = cat.subcategories.map(s => s.id);
    const otherSubIds = selectedSubcategoryIds.filter(id => !subIds.includes(id));

    // Sous-catégories retenues avant le clic (une catégorie entière les couvre toutes)
    const current = getCategoryState(cat) === 'all'
      ? subIds
      : selectedSubcategoryIds.filter(id => subIds.includes(id));
    const next = current.includes(subcategoryId)
      ? current.filter(id => id !== subcategoryId)
      : [...current, subcategoryId];

    // Plus aucune sous-catégorie retenue : la catégorie sort du budget
    if (next.length === 0) {
      applySelection(selectedCategoryIds.filter(id => id !== cat.id), otherSubIds);
      return;
    }

    const categoryIds = selectedCategoryIds.includes(cat.id)
      ? selectedCategoryIds
      : [...selectedCategoryIds, cat.id];

    // Toutes les sous-catégories retenues = catégorie entière, on ne les liste plus
    applySelection(
      categoryIds,
      next.length === subIds.length ? otherSubIds : [...otherSubIds, ...next]
    );
  };

  // Lignes affichées dans le dropdown, filtrées par la recherche.
  // La catégorie transmise aux handlers reste complète : seules les sous-catégories
  // affichées sont filtrées.
  const normalize = (value: string) =>
    value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const search = normalize(categorySearch.trim());
  const categoryRows = expenseCategories
    .map(cat => {
      if (!search) return { category: cat, subcategories: cat.subcategories };
      const matchesCategory = normalize(translateCategoryName(cat.id, cat.name, cat.isDefault)).includes(search);
      if (matchesCategory) return { category: cat, subcategories: cat.subcategories };
      const subs = cat.subcategories.filter(s =>
        normalize(translateCategoryName(s.id, s.name, s.isDefault)).includes(search)
      );
      return subs.length > 0 ? { category: cat, subcategories: subs } : null;
    })
    .filter((row): row is { category: Category; subcategories: Category['subcategories'] } => row !== null);

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  // Helper pour afficher le texte de la sélection (avec icône si une seule catégorie)
  const getSelectedCategoriesText = (): React.ReactNode => {
    if (selectedCategoryIds.length === 0) return t('budgetConfig.selectCategories');
    const categories = selectedCategoryIds
      .map(id => expenseCategories.find(c => c.id === id))
      .filter(Boolean) as Category[];
    if (categories.length === 1) {
      const cat = categories[0];
      const catName = translateCategoryName(cat.id, cat.name, cat.isDefault);
      const picked = cat.subcategories.filter(s => selectedSubcategoryIds.includes(s.id)).length;
      const label = getCategoryState(cat) === 'partial'
        ? `${catName} (${picked}/${cat.subcategories.length})`
        : catName;
      return cat.icon ? (
        <span className="flex items-center gap-2">
          <DynamicIcon name={cat.icon} className="w-4 h-4" />
          {label}
        </span>
      ) : label;
    }
    return t('budgetConfig.categoriesSelected', { count: categories.length });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation du compte bancaire ciblé
    if (hasAccounts && !selectedAccountId) {
      setError(t('budgetConfig.selectOneAccount'));
      return;
    }

    setSaving(true);

    try {
      // Construire le payload
      const payload: Partial<CreateBudgetDto> = {
        // Utiliser la somme des montants hebdomadaires si activé, sinon le montant saisi
        amount: useWeeklyAmounts ? weeklyTotal : formData.amount,
        period: formData.period,
        startDate: formData.startDate,
      };

      // Utiliser la multi-sélection si des catégories sont sélectionnées
      const categoryIds = formData.expenseCategoryIds || [];
      const subcategoryIds = formData.expenseSubcategoryIds || [];

      if (categoryIds.length > 0) {
        payload.expenseCategoryIds = categoryIds;
      }
      if (subcategoryIds.length > 0) {
        payload.expenseSubcategoryIds = subcategoryIds;
      }

      // Nom personnalisé (envoyer null explicitement si vide en édition)
      if (formData.name) {
        payload.name = formData.name;
      } else if (editingBudget) {
        payload.name = undefined; // Sera traité comme null côté backend
      }

      // Montants hebdomadaires (envoyer null explicitement si désactivé en édition)
      if (useWeeklyAmounts) {
        payload.weeklyAmounts = weeklyAmounts;
      } else if (editingBudget) {
        payload.weeklyAmounts = null;
      }

      // Compte bancaire ciblé
      if (selectedAccountId) {
        payload.bankAccountIds = [selectedAccountId];
      }

      // Date de fin (envoyer null explicitement si désactivé en édition)
      if (useEndDate && formData.endDate) {
        payload.endDate = formData.endDate;
      } else if (editingBudget) {
        payload.endDate = null;
      }

      if (editingBudget) {
        // Passer le mois/année d'édition pour le versionnement
        const queryParams = new URLSearchParams();
        queryParams.append('editMonth', displayMonth.toString());
        queryParams.append('editYear', displayYear.toString());
        await api.put(`/budgets/${editingBudget.id}?${queryParams.toString()}`, payload);
      } else {
        await api.post(`/budgets?type=${effectiveType}`, payload);
      }

      onSaveSuccess();
    } catch (err: unknown) {
      // Extraire le message d'erreur de la réponse API si disponible
      let errorMessage = t('budgetConfig.saveError');
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; code?: string } } };
        if (axiosError.response?.data?.code === 'PLAN_LIMIT_REACHED') {
          errorMessage = t('budgetConfig.planLimitReached');
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingBudget) return;
    if (!confirm(t('budgetConfig.deleteBudget'))) return;

    setDeleting(true);
    try {
      await api.delete(`/budgets/${editingBudget.id}`);
      onSaveSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('budgetConfig.deleteError');
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const selectAccount = (accountId: string) => {
    setSelectedAccountId(accountId);
    setLegacyMultiAccount(false);
  };

  // La nature du budget suit le compte retenu ; à défaut, celle de l'écran d'origine
  const accountKind = (accountId: string): 'personal' | 'shared' | null => {
    if (personalAccounts.some(a => a.id === accountId)) return 'personal';
    if (sharedAccounts.some(a => a.id === accountId)) return 'shared';
    return null;
  };
  const effectiveType = (selectedAccountId && accountKind(selectedAccountId)) || type;
  const hasAccounts = personalAccounts.length > 0 || sharedAccounts.length > 0;
  const showAccountGroups = personalAccounts.length > 0 && sharedAccounts.length > 0;

  const previewAmount = useWeeklyAmounts ? weeklyTotal : formData.amount;
  const monthlyPeriod = formData.period === 'monthly';

  return createPortal(
    <div className="prov-overlay">
      <div className="prov-backdrop" onClick={onClose} />
      <div className="prov-wrap">
        <div className="prov-card" role="dialog" aria-label={editingBudget ? t('budgetConfig.editBudget') : t('budgetConfig.newBudget')}>
          <header className="prov-head">
            <span className="prov-icon"><Target className="w-[22px] h-[22px]" /></span>
            <div className="min-w-0">
              <h2 className="prov-title">{editingBudget ? t('budgetConfig.editBudget') : t('budgetConfig.newBudget')}</h2>
              <p className="prov-sub">{t('budgetConfig.subtitle')}</p>
            </div>
            <button className="prov-close" onClick={onClose} aria-label={tc('close')}><X className="w-[18px] h-[18px]" /></button>
          </header>

          {!editingBudget && onSwitchType && (
            <div className="prov-tabs">
              <SegmentedControl
                block
                ariaLabel={t('provision.preview')}
                value="budget"
                onChange={(v) => { if (v === 'envelope') onSwitchType(); }}
                options={[
                  { value: 'envelope', label: t('provision.tabEnvelope') },
                  { value: 'budget', label: t('provision.tabBudget') },
                ]}
              />
            </div>
          )}

          <div className="prov-body">
            <form onSubmit={handleSubmit} className="prov-form">
              <div className="prov-fields">
                {error && <div className="prov-error">{error}</div>}
                {/* Alerte de versionnement */}
                {versioningInfo?.willVersion && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800 mb-1">{t('budgetConfig.versioningTitle')}</p>
                        <p className="text-amber-700">
                          <Trans t={t} i18nKey="budgetConfig.versioningMessage" values={{ oldEndDate: versioningInfo.oldEndDate, newStartDate: versioningInfo.newStartDate }} components={{ strong: <strong /> }} />
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {loadingData ? (
                  <div className="prov-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : (
              <>
                {/* Nom du budget (optionnel) — en tête */}
                <div>
                  <FloatingField
                    label={t('budgetConfig.optionalName')}
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    maxLength={100}
                  />
                  <p className="mt-1 text-xs" style={{ color: 'var(--text-3)' }}>{t('budgetConfig.customNameHint')}</p>
                </div>

            {/* Catégories et sous-catégories (arbre, multi-sélection) */}
            <div className="mb-4">
              <label className="field-label">
                {t('budgetConfig.categories')} *
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (categoryDropdownOpen) setCategorySearch('');
                    setCategoryDropdownOpen(!categoryDropdownOpen);
                  }}
                  className="prov-select"
                >
                  <span className={clsx(
                    "flex-1 truncate",
                    selectedCategoryIds.length === 0 && "ph"
                  )}>
                    {getSelectedCategoriesText()}
                  </span>
                  <ChevronDown className={clsx(
                    "prov-chev w-5 h-5",
                    categoryDropdownOpen && "open"
                  )} />
                </button>
                {categoryDropdownOpen && (
                  <div className="prov-pop">
                    <div className="prov-pop-search">
                      <Search className="w-4 h-4" />
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        placeholder={t('budgetConfig.searchCategories')}
                        autoFocus
                      />
                    </div>
                    {categoryRows.length === 0 && (
                      <p className="prov-pop-empty">{t('budgetConfig.noCategoryMatch')}</p>
                    )}
                    {categoryRows.map(({ category: cat, subcategories }) => {
                      const state = getCategoryState(cat);
                      const pickedCount = cat.subcategories.filter(s => selectedSubcategoryIds.includes(s.id)).length;
                      // Une recherche active déplie d'office pour montrer les sous-catégories trouvées
                      const isExpanded = !!search || expandedCategories.includes(cat.id);
                      return (
                        <div key={cat.id}>
                          <div className="prov-pop-row">
                            <OptionToggle
                              variant="row"
                              selected={state !== 'none'}
                              onToggle={() => handleCategoryToggle(cat)}
                              className="flex-1 min-w-0"
                            >
                              <span className="flex items-center gap-2">
                                {cat.icon && (
                                  <DynamicIcon name={cat.icon} className="w-4 h-4 flex-shrink-0 opacity-70" />
                                )}
                                <span className="truncate">
                                  {translateCategoryName(cat.id, cat.name, cat.isDefault)}
                                </span>
                                {state === 'partial' && (
                                  <span className="prov-pop-count">
                                    {pickedCount}/{cat.subcategories.length}
                                  </span>
                                )}
                              </span>
                            </OptionToggle>
                            {subcategories.length > 0 && (
                              <button
                                type="button"
                                className="prov-pop-exp"
                                onClick={() => toggleExpanded(cat.id)}
                                aria-expanded={isExpanded}
                                aria-label={t('budgetConfig.toggleSubcategories')}
                              >
                                <ChevronRight className={clsx('w-4 h-4', isExpanded && 'rotate-90')} />
                              </button>
                            )}
                          </div>
                          {isExpanded && subcategories.length > 0 && (
                            <div className="prov-pop-subs">
                              {subcategories.map(sub => (
                                <OptionToggle
                                  key={sub.id}
                                  variant="row"
                                  selected={isSubcategorySelected(cat, sub.id)}
                                  onToggle={() => handleSubcategoryToggle(cat, sub.id)}
                                >
                                  <span className="flex items-center gap-2">
                                    {sub.icon && (
                                      <DynamicIcon name={sub.icon} className="w-4 h-4 flex-shrink-0 opacity-70" />
                                    )}
                                    <span className="truncate">
                                      {translateCategoryName(sub.id, sub.name, sub.isDefault)}
                                    </span>
                                  </span>
                                </OptionToggle>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Badges du périmètre retenu : catégories entières puis sous-catégories */}
              {selectedCategoryIds.length > 0 && (
                <div className="prov-tags">
                  {selectedCategoryIds.map(catId => {
                    const cat = expenseCategories.find(c => c.id === catId);
                    if (!cat || getCategoryState(cat) !== 'all') return null;
                    return (
                      <span key={catId} className="prov-tag">
                        {cat.icon && <DynamicIcon name={cat.icon} className="ic w-3.5 h-3.5" />}
                        {translateCategoryName(cat.id, cat.name, cat.isDefault)}
                        <button
                          type="button"
                          onClick={() => handleCategoryToggle(cat)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  {selectedSubcategoryIds.map(subId => {
                    const sub = availableSubcategories.find(s => s.id === subId);
                    if (!sub) return null;
                    const cat = expenseCategories.find(c => c.id === sub.categoryId);
                    if (!cat) return null;
                    return (
                      <span key={subId} className="prov-tag">
                        {sub.icon && <DynamicIcon name={sub.icon} className="ic w-3.5 h-3.5" />}
                        {translateCategoryName(sub.id, sub.name, sub.isDefault)}
                        <button
                          type="button"
                          onClick={() => handleSubcategoryToggle(cat, subId)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>


            {/* Période */}
            <div className="mb-4">
              <span className="field-label">{t('budgetConfig.period')}</span>
              <SegmentedControl
                block
                ariaLabel={t('budgetConfig.period')}
                value={formData.period}
                onChange={(period) => {
                  setFormData({ ...formData, period });
                  if (period === 'weekly') setUseWeeklyAmounts(false);
                }}
                options={[
                  { value: 'monthly', label: t('budgetConfig.monthlyPeriod') },
                  { value: 'weekly', label: t('budgetConfig.weeklyPeriod') },
                ]}
              />
            </div>

            {/* Montant */}
            <div className="mb-4">
              <FloatingField
                label={`${formData.period === 'weekly' ? t('budgetConfig.weeklyAmount') : t('budgetConfig.monthlyAmount')} ${!useWeeklyAmounts ? '*' : ''}`}
                type="text"
                inputMode="decimal"
                suffix="€"
                className={clsx('num', useWeeklyAmounts && 'opacity-60 cursor-not-allowed')}
                value={useWeeklyAmounts ? weeklyTotal.toFixed(2) : (formData.amount || '')}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value.replace(',', '.')) || 0 })}
                required={!useWeeklyAmounts}
                disabled={useWeeklyAmounts}
              />
              {useWeeklyAmounts && (
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{t('budgetConfig.autoCalculated')}</p>
              )}
            </div>

            {/* Montants par semaine (optionnel, mensuel uniquement) */}
            {formData.period === 'monthly' && (
              <div className="flex flex-col gap-2">
                <SwitchToggle
                  selected={useWeeklyAmounts}
                  onToggle={() => setUseWeeklyAmounts(!useWeeklyAmounts)}
                  className="self-start"
                >
                  {t('budgetConfig.customWeeklyAmounts')}
                </SwitchToggle>
                {useWeeklyAmounts && (
                  <div className="prov-wk-grid pl-2">
                    {weeksOfMonth.map(({ week, start, end }) => (
                      <div key={week}>
                        <FloatingField
                          label={t('budgetConfig.week', { number: week })}
                          type="text"
                          inputMode="decimal"
                          suffix="€"
                          className="num"
                          value={weeklyAmounts[week] || ''}
                          onChange={(e) => setWeeklyAmounts({
                            ...weeklyAmounts,
                            [week]: parseFloat(e.target.value.replace(',', '.')) || 0
                          })}
                        />
                        <p className="prov-wk-range">{t('budgetConfig.weekRange', { start, end })}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Compte bancaire ciblé : le budget s'impute au solde de ce compte, il porte
                donc sur un seul compte, et sa nature suit celle du compte retenu */}
            {hasAccounts && (
              <div className="mb-4">
                <span className="field-label">{t('budgetConfig.bankAccount')} *</span>
                {legacyMultiAccount && (
                  <div className="p-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Layers className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-700">{t('budgetConfig.legacyAllAccounts')}</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  {([
                    { kind: 'personal' as const, label: t('budgetConfig.personalAccounts'), accounts: personalAccounts },
                    { kind: 'shared' as const, label: t('budgetConfig.sharedAccounts'), accounts: sharedAccounts },
                  ]).map(({ kind, label, accounts }) => {
                    if (accounts.length === 0) return null;
                    // La nature d'un budget existant ne se change pas : l'autre groupe est verrouillé
                    const locked = !!editingBudget && kind !== type;
                    return (
                      <div key={kind} className="flex flex-col gap-2">
                        {showAccountGroups && (
                          <span className="prov-acc-grp">{label}</span>
                        )}
                        {accounts.map(account => (
                          <OptionToggle
                            key={account.id}
                            selected={selectedAccountId === account.id}
                            onToggle={() => selectAccount(account.id)}
                            color={account.color}
                            icon={<Wallet className="w-[18px] h-[18px]" />}
                            disabled={locked}
                            title={locked ? t('budgetConfig.accountKindLocked') : undefined}
                          >
                            {account.name}
                          </OptionToggle>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-3)' }}>
                  {editingBudget ? t('budgetConfig.bankAccountHint') : t('budgetConfig.bankAccountKindHint')}
                </p>
              </div>
            )}

            {/* Dates — début + « Budget temporaire » sur la même ligne (largeurs
                fixes) ; la date de fin apparaît SOUS la date de début si activé.
                `flex-wrap` : sur mobile les deux ne tiennent pas côte à côte
                (libellé du switch en nowrap), l'interrupteur passe dessous. */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex flex-col items-start gap-3 shrink-0">
                <FloatingField
                  label={t('budgetConfig.startDate')}
                  type="date"
                  containerClassName="ff-fit"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
                {useEndDate && (
                  <FloatingField
                    label={t('budgetConfig.endDate')}
                    type="date"
                    containerClassName="ff-fit"
                    value={formData.endDate || ''}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                )}
              </div>
              <SwitchToggle selected={useEndDate} onToggle={() => setUseEndDate(!useEndDate)}>
                {t('budgetConfig.temporaryBudget')}
              </SwitchToggle>
            </div>

              </>
                )}
              </div>

              {/* Footer */}
              <div className="prov-foot">
                {editingBudget && (
                  <button type="button" onClick={handleDelete} disabled={deleting} className="btn btn-danger">
                    <Trash2 className="w-4 h-4" />
                    {deleting ? t('budgetConfig.deleting') : tc('delete')}
                  </button>
                )}
                <div className="foot-spacer" />
                <button type="button" onClick={onClose} className="btn btn-ghost">{tc('cancel')}</button>
                <button
                  type="submit"
                  disabled={saving || selectedCategoryIds.length === 0 || (hasAccounts && !selectedAccountId)}
                  className="btn btn-primary"
                >
                  {saving ? t('budgetConfig.saving') : (editingBudget ? tc('edit') : t('budgetConfig.create'))}
                </button>
              </div>
            </form>

            {/* Aperçu vivant budget */}
            <aside className="prov-preview">
              <span className="eyebrow">{t('provision.preview')}</span>
              <div className="prov-prev-card">
                <div className="prov-prev-top">
                  <div className="min-w-0">
                    <div className="prov-prev-name">
                      {formData.name || (selectedCategoryIds.length ? getSelectedCategoriesText() : t('budgetConfig.previewNoCategory'))}
                    </div>
                    <div className="prov-prev-sub">{monthlyPeriod ? t('budgetConfig.monthlyAmount') : t('budgetConfig.weeklyAmount')}</div>
                  </div>
                  <span className="prov-state credit">{t('budgetConfig.stateTracked')}</span>
                </div>
                <div>
                  <div className="prov-prev-fig"><b className="num">0 €</b><span>{t('budgetConfig.spentOf')} {fmt(previewAmount)} €</span></div>
                  <div className="prov-bar"><span style={{ width: '0%' }} /></div>
                </div>
                <div className="prov-prev-foot">
                  <span>{t('budgetConfig.remainingLabel')} <b className="num">{fmt(previewAmount)} €</b></span>
                  <span>{monthlyPeriod
                    ? t('budgetConfig.weeklyPace', { amount: `${fmt(previewAmount / 4.33)} €` })
                    : t('budgetConfig.monthlyPace', { amount: `${fmt(previewAmount * 4.33)} €` })}</span>
                </div>
              </div>
              <div className="prov-hint">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="9" /><path d="M12 8.2v.2M12 11.4v4.4" strokeLinecap="round" /></svg>
                <div>{t('budgetConfig.previewHint')}</div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
